import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { BsArrowLeft, BsPlusLg } from "react-icons/bs";
import { Skeleton } from "../../Components/Needed";
import ticketApi from "../../Helpers/ticketApi";
import { PRIORITIES, PRIORITY_LABEL } from "./shared";
import "../../Assets/css/tickets.css";

const KIND_LABEL = { customer: "Customer tickets", staff: "Staff tickets" };

// One category: its reasons (one per line), default priority and, for staff categories, on/off.
const CategoryRow = ({ kind, category, onSaved }) => {
    const [reasons, setReasons] = useState((category.reasons || []).join("\n"));
    const [name, setName] = useState(category.name || "");
    const [priority, setPriority] = useState(category.defaultPriority || "normal");
    const [enabled, setEnabled] = useState(category.enabled !== false);
    const [busy, setBusy] = useState(false);
    const isNew = !category.categoryId;
    const save = async () => {
        setBusy(true);
        const r = await ticketApi.saveCategory(kind, { ...(isNew ? {} : { categoryId: category.categoryId }), name, reasons: reasons.split("\n").map((x) => x.trim()).filter(Boolean), defaultPriority: priority, enabled });
        setBusy(false);
        if (!r.ok) { toast.error(r.message); return; }
        toast.success("Saved");
        onSaved();
    };
    return (
        <div className="tk-card">
            <div className="row g-2 align-items-start">
                <div className="col-md-3">
                    <label className="form-label small" htmlFor={`cat-name-${category.categoryId || "new"}`}>Category</label>
                    <input id={`cat-name-${category.categoryId || "new"}`} className="form-control form-control-sm" value={name} disabled={kind === "customer"} maxLength={60} onChange={(e) => setName(e.target.value)} />
                    {kind === "staff" ? (
                        <div className="form-check mt-2">
                            <input id={`cat-on-${category.categoryId || "new"}`} type="checkbox" className="form-check-input" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
                            <label className="form-check-label small" htmlFor={`cat-on-${category.categoryId || "new"}`}>Staff can choose it</label>
                        </div>
                    ) : null}
                </div>
                <div className="col-md-5">
                    <label className="form-label small" htmlFor={`cat-reasons-${category.categoryId || "new"}`}>Reasons (one per line)</label>
                    <textarea id={`cat-reasons-${category.categoryId || "new"}`} className="form-control form-control-sm" rows={4} value={reasons} onChange={(e) => setReasons(e.target.value)} />
                </div>
                <div className="col-md-2">
                    <label className="form-label small" htmlFor={`cat-pr-${category.categoryId || "new"}`}>Default priority</label>
                    <select id={`cat-pr-${category.categoryId || "new"}`} className="form-select form-select-sm" value={priority} onChange={(e) => setPriority(e.target.value)}>
                        {PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>)}
                    </select>
                </div>
                <div className="col-md-2 d-flex align-items-end h-100">
                    <button type="button" className="btn btn-primary btn-sm mt-md-4" disabled={busy || !name.trim()} onClick={save}>{isNew ? "Add" : "Save"}</button>
                </div>
            </div>
        </div>
    );
};

// The SLA table for one kind: a row for "every category" and one per category, a column per priority.
const SlaTable = ({ kind, table, onSaved }) => {
    const [cells, setCells] = useState({});
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        const next = {};
        (table?.rows || []).forEach((r) => { next[`${r.category}|${r.priority}`] = { m: r.firstResponseMinutes, h: r.resolutionHours, source: r.source }; });
        setCells(next);
    }, [table]);
    const categories = [...new Set((table?.rows || []).map((r) => r.category))];
    const edit = (key, field, value) => setCells((c) => ({ ...c, [key]: { ...c[key], [field]: value, dirty: true } }));
    const save = async () => {
        const rows = Object.entries(cells).filter(([, c]) => c.dirty).map(([key, c]) => {
            const [category, priority] = key.split("|");
            return { category, priority, firstResponseMinutes: Number(c.m), resolutionHours: Number(c.h) };
        });
        if (!rows.length) { toast.info("Nothing changed"); return; }
        setBusy(true);
        const r = await ticketApi.saveSla(kind, rows);
        setBusy(false);
        if (!r.ok) { toast.error(r.message); return; }
        toast.success("SLA saved");
        onSaved();
    };
    const clear = async (category, priority) => {
        const r = await ticketApi.saveSla(kind, [{ category, priority, clear: true }]);
        if (!r.ok) toast.error(r.message); else onSaved();
    };
    return (
        <div className="tk-card">
            <div className="table-responsive">
                <table className="table table-sm align-middle mb-2 tk-sla-table">
                    <caption className="small text-muted">First reply in minutes / resolution in hours. A category row overrides "Every category"; grey values are the built-in defaults.</caption>
                    <thead>
                        <tr><th scope="col">Category</th>{PRIORITIES.map((p) => <th scope="col" key={p}>{PRIORITY_LABEL[p]}</th>)}</tr>
                    </thead>
                    <tbody>
                        {categories.map((category) => (
                            <tr key={category}>
                                <th scope="row" className="fw-normal">{category === "*" ? "Every category" : category}</th>
                                {PRIORITIES.map((p) => {
                                    const key = `${category}|${p}`;
                                    const c = cells[key] || {};
                                    const own = (category === "*" ? c.source === "all" : c.source === "category") || c.dirty;
                                    return (
                                        <td key={p}>
                                            <div className={`d-flex gap-1 align-items-center ${own ? "" : "tk-inherited"}`}>
                                                <input type="number" min={1} className="form-control form-control-sm" style={{ width: 72 }} aria-label={`${category} ${p} first reply minutes`} value={c.m ?? ""} onChange={(e) => edit(key, "m", e.target.value)} />
                                                <span className="small">m</span>
                                                <input type="number" min={0.25} step={0.25} className="form-control form-control-sm" style={{ width: 64 }} aria-label={`${category} ${p} resolution hours`} value={c.h ?? ""} onChange={(e) => edit(key, "h", e.target.value)} />
                                                <span className="small">h</span>
                                                {own && !c.dirty ? <button type="button" className="btn btn-link btn-sm p-0 ms-1" title="Use the inherited value" onClick={() => clear(category, p)}>reset</button> : null}
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={save}>Save SLA</button>
        </div>
    );
};

const TicketSettings = () => {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [kind, setKind] = useState("customer");
    const [general, setGeneral] = useState({ autoCloseDays: 3, reopenDays: 7 });
    const [adding, setAdding] = useState(false);

    const load = useCallback(async () => {
        const r = await ticketApi.settings();
        if (!r.ok) { toast.error(r.message); return; }
        setData(r.data);
        setGeneral(r.data.general);
    }, []);
    useEffect(() => { load(); }, [load]);

    const saveGeneral = async (event) => {
        event.preventDefault();
        const r = await ticketApi.saveGeneral({ autoCloseDays: Number(general.autoCloseDays), reopenDays: Number(general.reopenDays) });
        if (!r.ok) toast.error(r.message); else { toast.success("Saved"); load(); }
    };

    if (!data) return <div className="settings-page"><Skeleton height={400} /></div>;

    return (
        <div className="settings-page tk-page">
            <button type="button" className="btn btn-link px-0 mb-2" onClick={() => navigate("/tickets")}><BsArrowLeft aria-hidden="true" /> Tickets</button>
            <h4 className="fw-semibold mb-1">Ticket settings</h4>
            <p className="text-muted">Categories and reasons the raiser picks from, SLA targets, and when resolved tickets close.</p>

            <section className="mb-4" aria-labelledby="tk-general">
                <h5 id="tk-general" className="fw-semibold">Closing and reopening</h5>
                <form className="tk-card d-flex flex-wrap gap-3 align-items-end" onSubmit={saveGeneral}>
                    <div>
                        <label className="form-label small" htmlFor="tk-autoclose">Close a resolved ticket after (days)</label>
                        <input id="tk-autoclose" type="number" min={1} max={60} className="form-control form-control-sm" value={general.autoCloseDays} onChange={(e) => setGeneral({ ...general, autoCloseDays: e.target.value })} />
                    </div>
                    <div>
                        <label className="form-label small" htmlFor="tk-reopen-days">Can be reopened for (days after resolved)</label>
                        <input id="tk-reopen-days" type="number" min={0} max={60} className="form-control form-control-sm" value={general.reopenDays} onChange={(e) => setGeneral({ ...general, reopenDays: e.target.value })} />
                    </div>
                    <button type="submit" className="btn btn-primary btn-sm">Save</button>
                </form>
            </section>

            <div className="tk-tabs mb-3" role="tablist" aria-label="Ticket kind">
                {["customer", "staff"].map((k) => (
                    <button key={k} type="button" role="tab" aria-selected={kind === k} className={`tk-tab ${kind === k ? "is-active" : ""}`} onClick={() => { setKind(k); setAdding(false); }}>{KIND_LABEL[k]}</button>
                ))}
            </div>

            <section className="mb-4" aria-labelledby="tk-cats">
                <div className="d-flex justify-content-between align-items-center">
                    <h5 id="tk-cats" className="fw-semibold mb-2">Categories and reasons</h5>
                    {kind === "staff" && !adding ? <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setAdding(true)}><BsPlusLg aria-hidden="true" /> Add category</button> : null}
                </div>
                {kind === "customer" ? <p className="small text-muted">Customers choose Account or Booking; a Booking ticket must name one of their bookings.</p> : <p className="small text-muted">Staff tickets go to the admins only.</p>}
                {adding ? <CategoryRow key="new" kind="staff" category={{ name: "", reasons: [], defaultPriority: "normal", enabled: true }} onSaved={() => { setAdding(false); load(); }} /> : null}
                {(data.categories[kind] || []).map((c) => <CategoryRow key={`${c.categoryId}-${c.updatedAt || ""}`} kind={kind} category={c} onSaved={load} />)}
            </section>

            <section aria-labelledby="tk-sla">
                <h5 id="tk-sla" className="fw-semibold mb-2">SLA targets</h5>
                <p className="small text-muted">At 80% of a target the person handling it (or the pool) is warned; at 100% the admins are told too. A customer ticket nobody took before its first-reply target moves to the Appealed queue. Time spent waiting on the raiser doesn't count.</p>
                <SlaTable kind={kind} table={data.sla[kind]} onSaved={load} />
            </section>
        </div>
    );
};

export default TicketSettings;
